import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { MeetingConsentKey, MeetingGuestState } from '@brimo/shared';
import { Banner, Button, Checkbox, Eyebrow, Skeleton, TextField } from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { ApiError, errorMessage } from '../../../lib/api';
import { PublicFrame } from '../../reservations/public/PublicFrame';
import { LevelMeter } from '../../telephony/LevelMeter';
import {
  consentBodyFor,
  guestConsent,
  guestDecline,
  guestKey,
  guestRoomToken,
  guestState,
  useGuestView,
  type GuestView,
} from '../api';
import { mediaProblemText, useLocalMedia, type LocalMedia } from '../media';
import { getRoomClient, type BrimoRoomClient, type BrimoRoomParticipant } from '../room-client';
import { HeaderEyebrow, HeaderPill, HeaderTitle, RoomHeader } from '../room/RoomHeader';
import { DevicePill, PillControl, RemoteAudio, RoundControl, TrackVideo, VideoTile } from '../room/parts';
import styles from './GuestPage.module.css';

/** The access key of the consent lives in the tab only (docs/dev/api-meetings.md „Guest"). */
const storageKey = (token: string) => `brimo.meeting.${token}`;
const POLL_MS = 3000;

function readKey(token: string): string | null {
  try {
    return window.sessionStorage.getItem(storageKey(token));
  } catch {
    return null;
  }
}

function writeKey(token: string, key: string | null): void {
  try {
    if (key === null) window.sessionStorage.removeItem(storageKey(token));
    else window.sessionStorage.setItem(storageKey(token), key);
  } catch {
    // private mode without storage: the key stays in memory for this page
  }
}

type Step = 'consent' | 'waiting' | 'call' | 'left' | 'ended' | 'declined';

function stepOf(state: MeetingGuestState): Step {
  if (state === 'admitted' || state === 'running') return 'call';
  if (state === 'waiting') return 'waiting';
  return state;
}

/**
 * `/m/:token` — the guest's side of the video room (no design frame; built from the public frame and the
 * lobby's dark room): the consent items with the name (four when the meeting records, two without
 * recording), the waiting room with the own camera, the call after „Zulassen", and the end pages.
 */
export function GuestPage() {
  const { token = '' } = useParams();
  const view = useGuestView(token);
  const queryClient = useQueryClient();
  const [key, setKey] = useState<string | null>(() => readKey(token));
  const [step, setStep] = useState<Step | null>(null);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const devices = useRef<ChosenDevices>({ cameraId: null, microphoneId: null });

  // Where the page starts: the stored key's state, else the state of the link.
  useEffect(() => {
    if (!view.data || step !== null) return;
    if (key === null) {
      setStep(stepOf(view.data.state));
      return;
    }
    guestState(token, key)
      .then((result) => {
        setRunning(result.state === 'running');
        setStep(stepOf(result.state));
      })
      .catch((error: unknown) => {
        writeKey(token, null);
        setKey(null);
        if (error instanceof ApiError && error.status === 403) setNotice(error.message);
        setStep(view.data ? stepOf(view.data.state) : 'consent');
      });
  }, [view.data, key, step, token]);

  // Waiting room and call follow the server every 3 s (admission, start, end). `consent` means the given
  // consent no longer covers what the meeting asks for (its event type records now): the page asks again with
  // the current items.
  useEffect(() => {
    if (key === null || (step !== 'waiting' && step !== 'call')) return;
    const timer = window.setInterval(() => {
      guestState(token, key)
        .then((result) => {
          setRunning(result.state === 'running');
          const next = stepOf(result.state);
          if (next === 'consent') void queryClient.invalidateQueries({ queryKey: guestKey(token) });
          setStep((current) => (current === next ? current : next));
        })
        .catch((error: unknown) => {
          if (error instanceof ApiError && error.status === 403) {
            writeKey(token, null);
            setKey(null);
            setNotice(error.message);
            setStep('consent');
          }
        });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [key, step, token, queryClient]);

  if (view.isPending || (view.data && step === null)) {
    return (
      <PublicFrame context="Videoberatung" width={620}>
        <div className={styles.skeleton} aria-busy="true" aria-label="Wird geladen">
          <Skeleton width="40%" height={14} />
          <Skeleton width="70%" height={26} />
          <Skeleton height={240} />
        </div>
      </PublicFrame>
    );
  }

  if (view.isError || !view.data) {
    const gone = view.error instanceof ApiError && view.error.status === 410;
    const notFound = view.error instanceof ApiError && view.error.status === 404;
    return (
      <PublicFrame context="Videoberatung" width={620}>
        <div className={styles.resultCard}>
          <Eyebrow as="div">{notFound ? 'Link nicht gefunden' : gone ? 'Link ungültig' : 'Fehler'}</Eyebrow>
          <h1 className={styles.resultTitle}>
            {notFound ? 'Dieser Link wurde nicht gefunden.' : errorMessage(view.error)}
          </h1>
          <p className={styles.resultText}>
            {notFound || gone
              ? 'Bitte prüfen Sie den Link aus Ihrer Nachricht. Bei Fragen hilft Ihnen Ihr Berater weiter.'
              : 'Bitte versuchen Sie es in einem Moment erneut.'}
          </p>
          {!notFound && !gone ? (
            <div className={styles.buttonRow}>
              <Button variant="secondary" onClick={() => void view.refetch()}>
                Erneut laden
              </Button>
            </div>
          ) : null}
        </div>
      </PublicFrame>
    );
  }

  const data = view.data;
  const refresh = () => queryClient.invalidateQueries({ queryKey: guestKey(token) });

  switch (step) {
    case 'waiting':
      return <WaitingStep view={data} devices={devices} />;
    case 'call':
      return key ? (
        <CallStep
          token={token}
          accessKey={key}
          view={data}
          running={running}
          devices={devices}
          onLeave={() => setStep('left')}
        />
      ) : null;
    case 'left':
      return (
        <ResultPage
          view={data}
          eyebrow="Meetingraum verlassen"
          title="Sie haben den Meetingraum verlassen."
          text={`Solange das Gespräch läuft, können Sie wieder beitreten.`}
          action={
            <Button onClick={() => setStep('call')} disabled={key === null}>
              Wieder beitreten
            </Button>
          }
        />
      );
    case 'ended':
      return (
        <ResultPage
          view={data}
          eyebrow="Gespräch beendet"
          title="Das Gespräch ist beendet."
          text={`Vielen Dank. ${data.appointment.advisorName} sendet Ihnen die Zusammenfassung nach der Freigabe per E-Mail.`}
        />
      );
    case 'declined':
      return (
        <ResultPage
          view={data}
          eyebrow="Keine Einwilligung"
          title={
            data.recording
              ? 'Sie haben der Aufzeichnung nicht zugestimmt.'
              : 'Sie haben den Bedingungen nicht zugestimmt.'
          }
          text={`${data.recording ? 'Ohne alle vier Bestätigungen' : 'Ohne beide Bestätigungen'} findet das Gespräch nicht als Videoberatung statt. ${data.appointment.advisorName} meldet sich bei Ihnen.`}
          action={
            <Button variant="secondary" onClick={() => setStep('consent')}>
              Doch zustimmen
            </Button>
          }
        />
      );
    case 'consent':
    case null:
      return (
        <ConsentStep
          token={token}
          view={data}
          notice={notice}
          onConsented={(accessKey, state) => {
            writeKey(token, accessKey);
            setKey(accessKey);
            setNotice(null);
            setStep(stepOf(state));
          }}
          onDeclined={() => {
            writeKey(token, null);
            setKey(null);
            void refresh();
            setStep('declined');
          }}
        />
      );
  }
}

function frameProps(view: GuestView) {
  return {
    advisor: { name: view.appointment.advisorName },
    organizationName: view.organization.name,
    legal: { imprintUrl: view.organization.imprintUrl, privacyUrl: view.organization.privacyUrl },
  };
}

function ResultPage({
  view,
  eyebrow,
  title,
  text,
  action,
}: {
  view: GuestView;
  eyebrow: string;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <PublicFrame context="Videoberatung" width={620} {...frameProps(view)}>
      <div className={styles.resultCard}>
        <Eyebrow as="div">{eyebrow}</Eyebrow>
        <h1 className={styles.resultTitle}>{title}</h1>
        <p className={styles.resultText}>{text}</p>
        {action ? <div className={styles.buttonRow}>{action}</div> : null}
      </div>
    </PublicFrame>
  );
}

function ConsentStep({
  token,
  view,
  notice,
  onConsented,
  onDeclined,
}: {
  token: string;
  view: GuestView;
  notice: string | null;
  onConsented: (accessKey: string, state: MeetingGuestState) => void;
  onDeclined: () => void;
}) {
  const [name, setName] = useState(view.guestName);
  const [checked, setChecked] = useState<Partial<Record<MeetingConsentKey, boolean>>>({});
  const [busy, setBusy] = useState<'join' | 'decline' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const items = view.consentItems;
  const all = items.length > 0 && items.every((item) => checked[item.key] === true);
  const ready = all && name.trim().length > 0;

  async function join(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    setBusy('join');
    setError(null);
    try {
      const result = await guestConsent(token, { name: name.trim(), ...consentBodyFor(items) });
      onConsented(result.accessKey, result.state);
    } catch (failure) {
      setError(errorMessage(failure));
      setBusy(null);
    }
  }

  async function decline() {
    setBusy('decline');
    setError(null);
    try {
      await guestDecline(token);
      onDeclined();
    } catch (failure) {
      setError(errorMessage(failure));
      setBusy(null);
    }
  }

  return (
    <PublicFrame context="Videoberatung" width={620} {...frameProps(view)}>
      <form className={styles.consentCard} onSubmit={(event) => void join(event)}>
        <Eyebrow as="div">Einwilligung · Pflicht</Eyebrow>
        <h1 className={styles.consentTitle}>
          {view.appointment.eventTypeName} mit {view.appointment.advisorName}
        </h1>
        <div className={styles.consentTime}>{view.appointment.timeLabel}</div>
        <p className={styles.consentIntro}>
          {view.recording
            ? 'Das Gespräch wird aufgezeichnet und transkribiert. Bitte bestätigen Sie die vier Punkte, dann kommen Sie in den Warteraum.'
            : 'Das Gespräch wird nicht aufgezeichnet. Bitte bestätigen Sie die beiden Punkte, dann kommen Sie in den Warteraum.'}
        </p>
        {notice ? <Banner tone="warning">{notice}</Banner> : null}
        <TextField
          label="Ihr Name"
          value={name}
          required
          autoComplete="name"
          maxLength={120}
          onChange={(event) => setName(event.target.value)}
        />
        <div className={styles.consentList}>
          {items.map((item) => (
            <Checkbox
              key={item.key}
              label={item.label}
              checked={checked[item.key] === true}
              onChange={(event) => setChecked((value) => ({ ...value, [item.key]: event.target.checked }))}
            />
          ))}
        </div>
        <p className={styles.consentNote}>{view.consentNote}</p>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <div className={styles.consentActions}>
          <Button type="submit" disabled={!ready || busy !== null} loading={busy === 'join'}>
            Beitreten
          </Button>
          <Button
            variant="secondary"
            disabled={busy !== null}
            loading={busy === 'decline'}
            onClick={() => void decline()}
          >
            Ich stimme nicht zu
          </Button>
        </div>
      </form>
    </PublicFrame>
  );
}

function DarkHeader({ view, end }: { view: GuestView; end?: ReactNode }) {
  return (
    <RoomHeader variant="lobby" end={end}>
      <HeaderEyebrow>DAMOKLES Meetingraum</HeaderEyebrow>
      <HeaderTitle>
        {view.appointment.eventTypeName} mit {view.appointment.advisorName}
      </HeaderTitle>
      <HeaderPill>{view.appointment.timeLabel}</HeaderPill>
    </RoomHeader>
  );
}

function DevicePills({ media }: { media: LocalMedia }) {
  return (
    <div className={styles.pills}>
      <DevicePill
        kind="Kamera"
        label={media.cameraLabel}
        devices={media.cameras}
        selectedId={media.cameraId}
        onSelect={media.setCameraId}
      />
      <DevicePill
        kind="Mikrofon"
        label={media.microphoneLabel}
        devices={media.microphones}
        selectedId={media.microphoneId}
        onSelect={media.setMicrophoneId}
      />
      <span className={styles.levelPill}>
        Pegel
        <LevelMeter
          local={media.stream}
          remote={null}
          active={media.stream !== null}
          className={styles.meter}
        />
      </span>
    </div>
  );
}

/** The devices chosen in the waiting room; the call publishes the same ones. */
interface ChosenDevices {
  cameraId: string | null;
  microphoneId: string | null;
}

function WaitingStep({ view, devices }: { view: GuestView; devices: MutableRefObject<ChosenDevices> }) {
  const media = useLocalMedia(true);
  useEffect(() => {
    devices.current = { cameraId: media.cameraId, microphoneId: media.microphoneId };
  }, [devices, media.cameraId, media.microphoneId]);
  return (
    <div className={styles.room}>
      <DarkHeader view={view} />
      <div className={styles.waitingBody}>
        <div className={styles.stage}>
          <VideoTile
            className={styles.preview}
            size="large"
            stream={media.stream}
            mirrored
            name="Sie"
            placeholder={media.problem ? mediaProblemText(media.problem) : null}
          />
          <DevicePills media={media} />
        </div>
        <section className={styles.waitingCard} aria-live="polite">
          <div className={styles.darkEyebrow}>Warteraum</div>
          <p className={styles.waitingText}>
            Sie sind im Warteraum. {view.appointment.advisorName} lässt Sie gleich herein.
          </p>
          <p className={styles.waitingHint}>
            Die Seite wechselt von selbst ins Gespräch. Prüfen Sie so lange Kamera und Mikrofon.
          </p>
        </section>
      </div>
    </div>
  );
}

function CallStep({
  token,
  accessKey,
  view,
  running,
  devices,
  onLeave,
}: {
  token: string;
  accessKey: string;
  view: GuestView;
  /** The host has started the conversation (head note „Aufzeichnung läuft" / „Gespräch läuft"). */
  running: boolean;
  devices: MutableRefObject<ChosenDevices>;
  onLeave: () => void;
}) {
  const mobile = useIsMobile();
  const clientRef = useRef<BrimoRoomClient | null>(null);
  const leavingRef = useRef(false);
  const [participants, setParticipants] = useState<BrimoRoomParticipant[]>([]);
  const [state, setState] = useState<{ phase: 'connecting' | 'connected' | 'failed'; error: string | null }>({
    phase: 'connecting',
    error: null,
  });
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);

  const release = useCallback(async () => {
    const client = clientRef.current;
    clientRef.current = null;
    if (client) await client.disconnect().catch(() => undefined);
  }, []);

  const connect = useCallback(async () => {
    if (clientRef.current) return;
    const client = getRoomClient();
    clientRef.current = client;
    const stale = () => clientRef.current !== client;
    setState({ phase: 'connecting', error: null });
    try {
      const credentials = await guestRoomToken(token, accessKey);
      if (stale()) return;
      const sync = () => setParticipants(client.participants());
      client.on('participants', sync);
      client.on('activeSpeaker', sync);
      client.on('disconnected', () => {
        if (leavingRef.current || stale()) return;
        clientRef.current = null;
        setState({ phase: 'failed', error: 'Die Verbindung zum Meetingraum ist unterbrochen.' });
      });
      await client.connect(credentials);
      if (stale()) {
        await client.disconnect().catch(() => undefined);
        return;
      }
      await client.setMicrophone(true, devices.current.microphoneId ?? undefined);
      await client.setCamera(true, devices.current.cameraId ?? undefined);
      sync();
      setState({ phase: 'connected', error: null });
    } catch (error) {
      if (stale()) return;
      await release();
      setState({ phase: 'failed', error: errorMessage(error) });
    }
  }, [token, accessKey, release, devices]);

  useEffect(() => {
    leavingRef.current = false;
    void connect();
    return () => {
      leavingRef.current = true;
      void release();
    };
  }, [connect, release]);

  const remote = participants.filter((participant) => !participant.isLocal);
  const host = remote[0] ?? null;
  const local = participants.find((participant) => participant.isLocal) ?? null;
  const hostTrack = host?.screenTrack ?? host?.videoTrack ?? null;
  const connected = state.phase === 'connected';
  const hostLabel = `${host?.name ?? view.appointment.advisorName}${host?.speaking ? ' · spricht' : ''}`;
  const placeholder =
    state.phase === 'connecting'
      ? 'Verbindung wird aufgebaut …'
      : `${view.appointment.advisorName} ist noch nicht im Gespräch.`;

  async function toggle(kind: 'mic' | 'cam') {
    const client = clientRef.current;
    if (!client) return;
    if (kind === 'mic') {
      await client.setMicrophone(!micOn, devices.current.microphoneId ?? undefined);
      setMicOn(!micOn);
    } else {
      await client.setCamera(!camOn, devices.current.cameraId ?? undefined);
      setCamOn(!camOn);
    }
  }

  async function leave() {
    leavingRef.current = true;
    await release();
    onLeave();
  }

  return (
    <div className={styles.room}>
      <RemoteAudio
        tracks={remote.flatMap((participant) => (participant.audioTrack ? [participant.audioTrack] : []))}
      />
      <DarkHeader
        view={view}
        end={running ? (view.recording ? 'Aufzeichnung läuft' : 'Gespräch läuft') : null}
      />
      <div className={styles.callBody}>
        {state.phase === 'failed' ? (
          <Banner
            tone="warning"
            actions={
              <Button size="small" variant="secondary" onClick={() => void connect()}>
                Erneut verbinden
              </Button>
            }
          >
            {state.error}
          </Banner>
        ) : null}
        {mobile ? (
          <div className={styles.mobileStage}>
            <VideoTile
              className={styles.mobileMain}
              track={hostTrack}
              name={hostLabel}
              active={host?.speaking ?? false}
              placeholder={placeholder}
            />
            <div className={styles.pip}>
              {local?.videoTrack ? <TrackVideo track={local.videoTrack} mirrored /> : null}
            </div>
          </div>
        ) : (
          <div className={styles.gallery}>
            <VideoTile
              track={hostTrack}
              name={hostLabel}
              active={host?.speaking ?? false}
              placeholder={placeholder}
            />
            <VideoTile
              track={local?.videoTrack ?? null}
              mirrored
              name="Sie"
              placeholder={camOn ? null : 'Kamera aus'}
            />
          </div>
        )}
        <div className={styles.controls} role="toolbar" aria-label="Gespräch">
          <RoundControl
            label="Mic"
            pressed={micOn}
            tone={micOn ? 'glass' : 'off'}
            size={mobile ? 'large' : 'default'}
            disabled={!connected}
            onClick={() => void toggle('mic')}
          />
          <RoundControl
            label="Cam"
            pressed={camOn}
            tone={camOn ? 'glass' : 'off'}
            size={mobile ? 'large' : 'default'}
            disabled={!connected}
            onClick={() => void toggle('cam')}
          />
          <PillControl tone="danger" onClick={() => void leave()}>
            Verlassen
          </PillControl>
        </div>
      </div>
    </div>
  );
}
